/**
 * LE TIROIR — ce qui n'est pas le texte se range, et s'ouvre d'un bouton (design §3).
 *
 * C'EST UN `<dialog>` NATIF, et ce choix vaut d'être écrit. Le navigateur sait déjà : piéger le
 * focus, rendre le reste de la page inerte, l'annoncer à un lecteur d'écran, fermer sur `Échap`, et
 * remettre le focus là où il était. Réécrire tout cela coûterait deux cents lignes et le ferait
 * moins bien — c'est le même raisonnement que le routeur en quarante lignes : on n'ajoute pas une
 * dépendance pour ce que la plateforme fait déjà.
 *
 * UN TIROIR NE S'OUVRE JAMAIS TOUT SEUL. Il n'a pas de `useEffect` qui décide d'apparaître : il
 * s'ouvre parce que `ouvert` est passé à vrai par un clic. Le §1 du design l'interdit et c'est la
 * seule règle absolue de ce composant — un panneau qui surgit pendant qu'on lit est une
 * interruption, et une information manquée dans un jeu à permadeath est une mort.
 */
import { useEffect, useRef } from "react";

import { Glyphe } from "./Glyphe.js";
import { t } from "../lexique/langue.js";

export function Popin({
  ouvert,
  titre,
  glyphe,
  large = false,
  surFermeture,
  children
}: {
  ouvert: boolean;
  titre: string;
  glyphe?: React.ReactNode;
  /** Une carte veut toute la place ; un sac se contente d'une colonne. */
  large?: boolean;
  surFermeture: () => void;
  children: React.ReactNode;
}) {
  const cadre = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = cadre.current;
    if (!element) return;
    if (ouvert && !element.open) element.showModal();
    if (!ouvert && element.open) element.close();
  }, [ouvert]);

  // Le contenu ne se monte pas tant que le tiroir est fermé : la carte est un SVG de trois cents
  // hexagones, et le calculer pour ne pas le montrer coûterait à chaque nœud lu.
  if (!ouvert) return <dialog ref={cadre} className={large ? "tiroir tiroir--large" : "tiroir"} />;

  return (
    <dialog
      ref={cadre}
      className={large ? "tiroir tiroir--large" : "tiroir"}
      aria-label={titre}
      // `Échap` ferme le dialogue lui-même ; on en informe React, sans quoi l'état resterait
      // « ouvert » et le tiroir refuserait de se rouvrir au clic suivant.
      onCancel={(evenement) => {
        evenement.preventDefault();
        surFermeture();
      }}
      onClose={surFermeture}
      // Un clic HORS du cadre ferme : la cible de l'événement est alors le dialogue lui-même, et
      // non l'un de ses enfants. C'est la seule façon de distinguer les deux sans écouter le
      // document entier.
      onClick={(evenement) => {
        if (evenement.target === cadre.current) surFermeture();
      }}
    >
      <div className="tiroir__cadre">
        <header className="tiroir__tete">
          <span className="rang rang--serre">
            {glyphe}
            <span className="capitales">{titre}</span>
          </span>
          <button type="button" className="puce puce--sourde" onClick={surFermeture} aria-label={t("Refermer")}>
            <Glyphe nom="croix" taille={14} />
          </button>
        </header>
        <div className="tiroir__corps">{children}</div>
      </div>
    </dialog>
  );
}
